import { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { Flex } from '@revenuemarket/cds';
import { ICellEditorComp } from 'ag-grid-community';
import { CustomCellEditorProps } from 'ag-grid-react';

import { LabeledCardBillingTransaction } from '~apis/card';
import { LabeledBankAccountTransaction } from '~apis/cash-flow-overview/transactions/getTransactions';
import { LabelValue } from '~components/CashStatus/LabelSelect';
import CategoryLabelTreeSelect from '~components/Labeling/LabelManagement/CategoryLabelTreeSelect';
import { flattenTree } from '~components/ui/HorizontalTreeTable/Table';
import { useCategoriesTree } from '~hooks/queries/categories/query';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { CashFlowType } from '~models/cashFlow';

export interface LabeledCardBilling extends LabeledCardBillingTransaction {
  transactionOutAmount?: number;
  isUnclassified?: boolean;
  accountId?: never;
  transactionId?: never;
  labelCount?: never;
  labelKey?: never;
  allLabelIds?: never;
}

interface Props
  extends CustomCellEditorProps<
    LabeledBankAccountTransaction | LabeledCardBilling,
    LabelValue | undefined
  > {
  cashFlowType?: CashFlowType;
  type?: 'CARD' | 'BANK_TRANSACTION';
  excludeCategoyKeys?: string[];
  hideSplitLabel?: boolean;
  filterCardLabel?: boolean;
}

function getTransactionType(data: LabeledBankAccountTransaction | LabeledCardBilling) {
  let transactionType;

  if ('paymentAmount' in data) {
    // If paymentAmount exists, treat it as LabeledCardBilling
    transactionType = data.paymentAmount >= 0 ? CashFlowType.OUT : CashFlowType.IN;
  } else {
    // If paymentAmount does not exist, treat it as LabeledBankAccountTransaction
    transactionType = (data.transactionOutAmount ?? 0) > 0 ? CashFlowType.OUT : CashFlowType.IN;
  }

  return transactionType;
}

const AGGridTransactionLabelSelectEditor = (
  { data, api, column, node, value, cashFlowType, filterCardLabel, onValueChange }: Props,
  ref: ForwardedRef<ICellEditorComp>,
) => {
  const { companyId } = useDemoAwareCompanyId();
  const { data: categoriesData } = useCategoriesTree({ companyId });
  const categories = categoriesData?.Data?.data?.tree?.children;
  const cashFlowTypeForTransaction = cashFlowType || getTransactionType(data);
  const flattenedTree = flattenTree(categories ?? []).filter(
    (item) =>
      !!item.value?.name &&
      (cashFlowTypeForTransaction ? item.value?.cashFlowType === cashFlowTypeForTransaction : true),
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const [innerValue, setValue] = useState(value ?? undefined);
  const [open, setOpen] = useState(true);

  useImperativeHandle(
    ref,
    () => ({
      getGui: () => {
        return containerRef.current ?? document.body;
      },
      getValue: () => {
        return innerValue;
      },
      init: (params) => {
        setValue(params.value);
      },
    }),
    [innerValue],
  );

  useEffect(() => {
    if (!open) {
      api.stopEditing(false);
      api.setFocusedCell(node.rowIndex as number, column);
    }
  }, [open, api, node.rowIndex, column]);

  if (data == null) {
    return null;
  }

  const transaction = data;

  const targetValue = flattenedTree.find((item) => item.value?.categoryLabelKey === innerValue);

  return (
    <Flex ref={containerRef} align="center" style={{ height: '100%' }}>
      <CategoryLabelTreeSelect
        filterCardLabel={filterCardLabel}
        cashFlowType={cashFlowTypeForTransaction}
        onSelect={(categoryLabelKey) => {
          onValueChange?.(categoryLabelKey);
          setValue(categoryLabelKey);
          setOpen(false);
        }}
        transactionId={transaction.transactionId}
        accountId={transaction.accountId}
        defaultActiveKey={targetValue?.key}
        transaction={transaction}
        targetValue={targetValue}
      />
    </Flex>
  );
};

export default forwardRef(AGGridTransactionLabelSelectEditor);
